const SCREENS = [
  {
    src: '/app-preview/statbox-preview-1.jpeg',
    alt: 'Statbox match performance rating on a phone',
    label: 'Match rating',
  },
  {
    src: '/app-preview/statbox-preview-3.jpeg',
    alt: 'Statbox game highlights and score on a phone',
    label: 'Highlights',
    featured: true,
  },
  {
    src: '/app-preview/statbox-preview-2.jpeg',
    alt: 'Statbox physical stats and heart rate on a phone',
    label: 'Physical stats',
  },
] as const

// Titanium-style rim: a light source from the top-left catches the metal edge.
const RIM_GRADIENT =
  'linear-gradient(155deg, #8a8a90 0%, #45454b 16%, #26262b 38%, #55555c 58%, #1d1d21 80%, #6e6e75 100%)'

const BUTTON_GRADIENT =
  'linear-gradient(180deg, #606067 0%, #2e2e33 45%, #4a4a51 100%)'

export function AppPhoneScreen({
  src,
  alt,
  featured = false,
  priority = false,
  className = '',
}: {
  src: string
  alt: string
  featured?: boolean
  priority?: boolean
  className?: string
}) {
  return <PhoneBezel src={src} alt={alt} featured={featured} priority={priority} className={className} />
}

function PhoneBezel({
  src,
  alt,
  className = '',
  featured = false,
  priority = false,
}: {
  src: string
  alt: string
  className?: string
  featured?: boolean
  priority?: boolean
}) {
  return (
    <div className={['relative', className].join(' ')}>
      {/* Side buttons — volume (left), power (right) */}
      <div
        aria-hidden
        className="absolute -left-[2px] top-[17%] h-[5.5%] w-[2.5px] rounded-l-[2px]"
        style={{ background: BUTTON_GRADIENT }}
      />
      <div
        aria-hidden
        className="absolute -left-[2px] top-[25%] h-[5.5%] w-[2.5px] rounded-l-[2px]"
        style={{ background: BUTTON_GRADIENT }}
      />
      <div
        aria-hidden
        className="absolute -right-[2px] top-[20%] h-[9%] w-[2.5px] rounded-r-[2px]"
        style={{ background: BUTTON_GRADIENT }}
      />

      {/* Metallic frame */}
      <div
        className={[
          'relative overflow-hidden p-[2px]',
          featured
            ? 'rounded-[30px] sm:rounded-[34px] shadow-[0_34px_70px_-18px_rgba(0,0,0,0.85),0_12px_28px_-12px_rgba(0,0,0,0.6),0_0_60px_-18px_rgba(113,205,217,0.28)]'
            : 'rounded-[24px] sm:rounded-[28px] shadow-[0_26px_54px_-16px_rgba(0,0,0,0.8),0_8px_20px_-10px_rgba(0,0,0,0.55)]',
        ].join(' ')}
        style={{ background: RIM_GRADIENT }}
      >
        {/* Inner bezel */}
        <div
          className={[
            'relative overflow-hidden bg-[#08080a]',
            featured
              ? 'rounded-[28px] p-[6px] sm:rounded-[32px] sm:p-[7px]'
              : 'rounded-[22px] p-[5px] sm:rounded-[26px] sm:p-[6px]',
          ].join(' ')}
        >
          {/* Dynamic Island with camera dot */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-[9px] z-10 flex h-[10px] w-[24%] -translate-x-1/2 items-center justify-end rounded-full bg-black pr-[8%] sm:top-[11px] sm:h-[12px]"
            style={{ boxShadow: 'inset 0 0 2px rgba(255,255,255,0.14)' }}
          >
            <span
              className="block h-[4px] w-[4px] rounded-full sm:h-[5px] sm:w-[5px]"
              style={{
                background:
                  'radial-gradient(circle at 35% 35%, #2e3f52 0%, #0c1118 65%)',
              }}
            />
          </div>

          <img
            src={src}
            alt={alt}
            width={739}
            height={1600}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            {...(priority && featured ? { fetchPriority: 'high' as const } : {})}
            className={[
              'block h-auto w-full object-cover object-top bg-[#0b0b0d]',
              featured
                ? 'rounded-[22px] sm:rounded-[26px]'
                : 'rounded-[17px] sm:rounded-[20px]',
            ].join(' ')}
            style={{ aspectRatio: '739 / 1600' }}
          />

          {/* Glass: hairline screen edge + diagonal glare */}
          <div
            aria-hidden
            className={[
              'pointer-events-none absolute',
              featured
                ? 'inset-[6px] rounded-[22px] sm:inset-[7px] sm:rounded-[26px]'
                : 'inset-[5px] rounded-[17px] sm:inset-[6px] sm:rounded-[20px]',
            ].join(' ')}
            style={{
              background:
                'linear-gradient(118deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.025) 22%, transparent 40%)',
              boxShadow:
                'inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 1px rgba(255,255,255,0.05)',
            }}
          />
        </div>
      </div>
    </div>
  )
}

type AppPhoneTrioProps = {
  className?: string
  priority?: boolean
  /** Scaled landing-page trio for inline previews. */
  compact?: boolean
  /** Larger landing-page trio for empty states. */
  preview?: boolean
  /** Always the short three-phone row, including on wide screens. */
  row?: boolean
}

function PhoneTrioStage({
  priority,
  showCaptions,
}: {
  priority: boolean
  showCaptions: boolean
}) {
  return (
    <div className="relative h-[470px] w-full max-w-[520px]">
      {SCREENS.map((screen, index) => {
        const featured = !!screen.featured
        const side = index === 0 ? 'left' : index === 2 ? 'right' : 'center'
        const position =
          side === 'left'
            ? 'absolute left-0 top-[48px] z-10 w-[168px]'
            : side === 'right'
              ? 'absolute right-0 top-[48px] z-10 w-[168px]'
              : 'absolute left-1/2 top-0 z-20 w-[210px] -translate-x-1/2'
        const tilt =
          side === 'left'
            ? '-rotate-[9deg]'
            : side === 'right'
              ? 'rotate-[9deg]'
              : ''
        return (
          <div key={screen.src} className={position}>
            <div
              className={[featured ? 'phone-float' : '', tilt, tilt && 'origin-bottom']
                .filter(Boolean)
                .join(' ')}
            >
              <PhoneBezel
                src={screen.src}
                alt={screen.alt}
                featured={featured}
                priority={priority}
                className={featured ? '' : '[filter:brightness(0.82)_saturate(0.95)]'}
              />
            </div>
            {showCaptions && (
              <p
                className={['phone-caption mt-3 text-center', tilt, tilt && 'origin-top']
                  .filter(Boolean)
                  .join(' ')}
              >
                {screen.label}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

function PhoneRow({ priority }: { priority: boolean }) {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(113,205,217,0.13) 0%, transparent 65%)',
          filter: 'blur(24px)',
        }}
      />
      <div className="relative flex items-end justify-center gap-2 sm:gap-3">
        {SCREENS.map((screen) => (
          <div
            key={screen.src}
            className={
              screen.featured
                ? 'w-[30%] max-w-[118px] translate-y-0'
                : 'w-[26%] max-w-[96px] translate-y-1'
            }
          >
            <PhoneBezel
              src={screen.src}
              alt={screen.alt}
              featured={screen.featured}
              priority={priority}
              className={screen.featured ? '' : '[filter:brightness(0.88)]'}
            />
            <p className="phone-caption mt-2.5 whitespace-nowrap text-center leading-tight max-sm:!text-[11px] max-sm:!tracking-[0.08em]">
              {screen.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

export function AppPhoneTrio({ className = '', priority = false, compact = false, preview = false, row = false }: AppPhoneTrioProps) {
  if (compact || preview) {
    const frame = preview
      ? 'relative mx-auto h-[210px] w-[234px]'
      : 'relative h-[132px] w-[148px] shrink-0'
    const scale = preview ? 'scale-[0.45]' : 'scale-[0.28]'
    return (
      <div className={[frame, className].join(' ')} aria-hidden>
        <div className={['pointer-events-none absolute left-0 top-0 w-[520px] origin-top-left', scale].join(' ')}>
          <PhoneTrioStage priority={priority} showCaptions={false} />
        </div>
      </div>
    )
  }

  if (row) {
    return (
      <div className={className}>
        <PhoneRow priority={priority} />
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="lg:hidden">
        <PhoneRow priority={priority} />
      </div>

      {/* Desktop: overlapping trio, center phone forward */}
      <div className="relative mx-auto hidden w-full max-w-[520px] lg:block">
        {/* Ambient glow behind the trio */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-[42%] h-[420px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(113,205,217,0.15) 0%, rgba(113,205,217,0.05) 40%, transparent 68%)',
            filter: 'blur(28px)',
          }}
        />
        {/* Floor shadow anchoring the phones */}
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-[52px] left-1/2 h-[26px] w-[78%] -translate-x-1/2 rounded-[50%]"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, transparent 70%)',
            filter: 'blur(10px)',
          }}
        />
        <PhoneTrioStage priority={priority} showCaptions />
      </div>
    </div>
  )
}
